import React from 'react';
import { cn } from '../lib/cn';

import { ReactComponent as AccessDenied } from './svg/access-denied.svg';
import { ReactComponent as AwaitingApproval } from './svg/awaiting-approval.svg';
import { ReactComponent as CartIsEmpty } from './svg/cart-is-empty.svg';
import { ReactComponent as CheckoutHandoffExpired } from './svg/checkout-handoff-expired.svg';
import { ReactComponent as FiltersMatchedNothing } from './svg/filters-matched-nothing.svg';
import { ReactComponent as NoAccountsYet } from './svg/no-accounts-yet.svg';
import { ReactComponent as NoAddressesYet } from './svg/no-addresses-yet.svg';
import { ReactComponent as NoApplicationsYet } from './svg/no-applications-yet.svg';
import { ReactComponent as NoEndpointYet } from './svg/no-endpoint-yet.svg';
import { ReactComponent as NoListingsYet } from './svg/no-listings-yet.svg';
import { ReactComponent as NoOrdersYet } from './svg/no-orders-yet.svg';
import { ReactComponent as NoPaymentMethodsYet } from './svg/no-payment-methods-yet.svg';
import { ReactComponent as NoPayoutsYet } from './svg/no-payouts-yet.svg';
import { ReactComponent as NoSavedItemsYet } from './svg/no-saved-items-yet.svg';
import { ReactComponent as NotFound } from './svg/not-found.svg';
import { ReactComponent as NothingCataloguedYet } from './svg/nothing-catalogued-yet.svg';
import { ReactComponent as RequestFailed } from './svg/request-failed.svg';
import { ReactComponent as SearchMatchedNothing } from './svg/search-matched-nothing.svg';
import { ReactComponent as SearchNotStarted } from './svg/search-not-started.svg';

import './illustrations.css';

/**
 * Illustration, the artwork for a whole-surface empty state.
 *
 * Keyed by SCENARIO, never by artwork. `<Illustration name="request-failed" />`
 * says what the state means; which picture draws it is this file's business and
 * nobody else's. Replacing the art is then a change here and in `svg/`, and no
 * call site moves. See README.md.
 *
 * SIZE. The default 112px is the reason this component exists. The library's
 * `.dsk-empty__icon` renders a 24px glyph in a 56px disc, which is right for a
 * system icon and hopeless for isometric line art: the strokes fall to a
 * fraction of a pixel and the whole thing turns to grey mush. Anything below
 * about 96px, use `Icon` and a Phosphor glyph instead.
 *
 * COLOUR. Every stroke in every file is `currentColor`, so an illustration
 * takes the colour of the text around it and is correct in both themes with
 * nothing passed. Never set a fill.
 *
 * The art is NOT square, and the viewBoxes differ per file. The CSS sizes the
 * BOX and lets `preserveAspectRatio` do the rest, so a fixed square width and
 * height would distort it.
 */

const ART = {
  'access-denied': AccessDenied,
  'awaiting-approval': AwaitingApproval,
  'cart-is-empty': CartIsEmpty,
  'checkout-handoff-expired': CheckoutHandoffExpired,
  'filters-matched-nothing': FiltersMatchedNothing,
  'no-accounts-yet': NoAccountsYet,
  'no-addresses-yet': NoAddressesYet,
  'no-applications-yet': NoApplicationsYet,
  'no-endpoint-yet': NoEndpointYet,
  'no-listings-yet': NoListingsYet,
  'no-orders-yet': NoOrdersYet,
  'no-payment-methods-yet': NoPaymentMethodsYet,
  'no-payouts-yet': NoPayoutsYet,
  'no-saved-items-yet': NoSavedItemsYet,
  'not-found': NotFound,
  'nothing-catalogued-yet': NothingCataloguedYet,
  'request-failed': RequestFailed,
  'search-matched-nothing': SearchMatchedNothing,
  'search-not-started': SearchNotStarted,
};

/** Every scenario slug that has artwork. Useful for a gallery or a test. */
export const ILLUSTRATION_NAMES = Object.keys(ART);

export function Illustration({ name, size = 'md', className, ...rest }) {
  const Art = ART[name];

  // An unknown slug renders nothing rather than throwing. An empty state is
  // already the "something is missing" surface; crashing the page because its
  // decoration is missing would turn a quiet gap into an outage.
  if (!Art) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(
        `<Illustration name="${name}"> has no artwork. Known names: ${ILLUSTRATION_NAMES.join(', ')}`,
      );
    }
    return null;
  }

  const sizeClass = { xs: 'dsa-illus--xs', sm: 'dsa-illus--sm', md: null, lg: 'dsa-illus--lg' }[size] || null;

  return (
    <span className={cn('dsa-illus', sizeClass, className)} aria-hidden="true" {...rest}>
      <Art />
    </span>
  );
}

export default Illustration;
